import Button from '@components/ui/ButtonV2/Button'
import CheckboxV2 from '@components/ui/CheckboxV2/CheckboxV2'
import InputV2 from '@components/ui/InputV2/InputV2'
import LineClamp from '@components/ui/LineClamp/LineClamp'
import { selectCountry } from '@redux/globalSlice'
import clsx from 'clsx'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { FC, FormEventHandler, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import TrustedHtml from '../TrustedHtml/TrustedHtml'
import s from './EmailForm.module.scss'

type CheckBox = {
  required: boolean
  value: string
  label: string
}
export type EmailFormData = {
  email: string
  recipientName: string
  fromName: string
  fromEmail: string
  personalMessage: string

  checkboxes: CheckBox[]
  language: string
  market: string
  geoCountry: string
  url: string
  urlLastClick: string
  source: string
}
export type EmailFormProps = {
  title: string
  source: string
  submitSuccessFullMsg: string
  placeholder: string
  checkboxes: CheckBox[]
  submitBtnText: string
  submitBtnPosition?: 'left' | 'center' | 'right'
  termsText?: string
  termsReadMoreText?: string

  labelTo: string
  labelRecipientsName: string
  labelYourFullName: string
  labelFromEmail: string
  labelFrom: string
  labelPersonalMessage: string
  onSubmit:
    | ((data: EmailFormData) => Promise<void>)
    | FormEventHandler<HTMLDivElement>
}

const EmailForm: FC<EmailFormProps> = ({
  submitSuccessFullMsg: isSubmitSuccesfulMsg,
  placeholder,
  submitBtnText,
  title,
  source,
  checkboxes,
  termsText,
  termsReadMoreText,
  submitBtnPosition = 'left',

  labelTo,
  labelRecipientsName,
  labelYourFullName,
  labelFromEmail,
  labelFrom,
  labelPersonalMessage,
  onSubmit: handleSubmitData,
}) => {
  const router = useRouter()

  const country = useSelector(selectCountry)
  const form = useForm()

  const [currentStep, setCurrentStep] = useState(1)

  const handleNextStep = async () => {
    const isValid = await form.trigger([
      'recipientName',
      'email',
      'personalMessage',
    ])
    if (isValid) {
      setCurrentStep(2)
    }
  }

  const onSubmit = async (data: EmailFormData) => {
    const urls = window.dataLayer.filter((x: any) => x.event == 'pageview')
    const lastUrl = urls.length > 1 ? urls[urls.length - 2].pagePath : ''
    const url = urls[urls.length - 1].pagePath
    await handleSubmitData({
      ...data,
      language: router.query.language as string,
      market: country?.code,
      geoCountry: Cookies.get('homeCountry') ?? '',
      url: url,
      urlLastClick: lastUrl,
      source: source,
    } as any)
  }

  return (
    <div className={s.root}>
      <FormProvider {...form}>
        {form.formState.isSubmitSuccessful ? (
          <TrustedHtml html={isSubmitSuccesfulMsg} />
        ) : (
          <>
            <TrustedHtml html={title} />
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div
                className={clsx(
                  { [s.hidden]: currentStep !== 1 },
                  s.inputGroup,
                )}
              >
                <div className={s.inputRow}>
                  <InputV2
                    id="recipientName"
                    placeholder={labelRecipientsName}
                    required
                  />
                  <InputV2 id="email" placeholder={placeholder} required />
                </div>
                <InputV2
                  id="personalMessage"
                  placeholder={labelPersonalMessage}
                  textarea
                />
                <Button
                  type="button"
                  variant={'secondary'}
                  onClick={handleNextStep}
                >
                  NEXT
                </Button>
              </div>
              <div
                className={clsx(s.inputGroup, {
                  [s.hidden]: currentStep !== 2,
                })}
              >
                <div className={s.inputRow}>
                  <InputV2
                    id="fromName"
                    placeholder={labelYourFullName}
                    required
                  />

                  <InputV2
                    id="fromEmail"
                    type="email"
                    placeholder={labelFromEmail}
                    required
                  />
                </div>

                {checkboxes.map((checkbox, index) => (
                  <CheckboxV2
                    key={index}
                    {...form.register(`checkboxes`)}
                    {...checkbox}
                  >
                    <TrustedHtml
                      style={{ maxWidth: '400px' }}
                      html={checkbox.label}
                    />
                  </CheckboxV2>
                ))}
                {termsText && (
                  <LineClamp
                    text={termsText}
                    readMoreText={termsReadMoreText}
                    textClassname={s.terms}
                  />
                )}
                <br />
                <Button variant="secondary">{submitBtnText}</Button>
              </div>
            </form>
          </>
        )}
      </FormProvider>
    </div>
  )
}

export default EmailForm
